/**
 * Draws the ninja as filled capsules from the pose solver's skeleton points:
 * far leg, far arm, torso, sash, near leg, near arm, head, headband. A solid
 * silhouette with a colored sash and headband tails, after the reference
 * sheet: broad chest, narrow waist, muscular limbs, fists, boots, a wide
 * headband at eye level with long tails. On the night world the body is paper
 * (the sheet's black silhouette would vanish), with a soft halo in the
 * headband color behind it and a mark on the ground under the feet that fades
 * with height, so the body and its height above the ground read at a glance.
 * Proportions come from the render feel so the owner can dial the look live.
 * All sprites are created once.
 */

import { S, type Sim } from "@lache/sim";
import { Container, type Sprite } from "pixi.js";
import type { RenderFeel } from "../feel";
import { NEON } from "../palette";
import { type CapsuleShape, makeCapsule, type ShapeFactory, setCapsule, setDisc } from "../shapes";
import type { ViewState } from "../view";
import { LADDER_BAR_HEIGHT, LADDER_BAR_WIDTH } from "../world";
import { defaultPoses, type Pose, type PoseName } from "./poses";
import { Ribbon } from "./ribbon";
import { BONES } from "./skeleton";
import { PoseSolver, type SkeletonPoints } from "./solver";

/** The body's color: paper on the night world (`setInk` recolors it). */
export const BODY = NEON.paper;
/** The reference sheet's black, kept for a body drawn on a light ground. */
export const INK = 0x141414;
export const RED = 0xd0342c;

/**
 * Headband colors a player can pick on the join screen of a split-screen race (the sash, the
 * headband, its tails, the halo, and the ground mark all take the color). The first is the
 * ninja's own red. Every color shows on the paper body and over the night sky, so there is no
 * white and no black.
 */
export const HEADBANDS: readonly { name: string; color: number }[] = [
  { name: "red", color: RED },
  { name: "blue", color: 0x3d7bff },
  { name: "green", color: 0x2fc25f },
  { name: "gold", color: 0xffc233 },
  { name: "purple", color: 0x9d4edd },
  { name: "orange", color: 0xff8c1a },
  { name: "cyan", color: NEON.cyan },
  { name: "pink", color: NEON.pink },
];

/** The headband entry for an index, wrapping so any integer picks one. */
export function headband(index: number): { name: string; color: number } {
  const n = HEADBANDS.length;
  return HEADBANDS[((Math.round(index) % n) + n) % n]!;
}

interface Limb {
  upper: CapsuleShape;
  lower: CapsuleShape;
  tip: CapsuleShape;
}

/** A capsule and its rim: the rim is the same capsule, wider by the outline, drawn behind every fill. */
function setRimmed(
  c: CapsuleShape,
  rim: CapsuleShape,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  r: number,
  outline: number,
): void {
  setCapsule(c, x0, y0, x1, y1, r);
  setCapsule(rim, x0, y0, x1, y1, r + outline);
}

export class SilhouetteRenderer {
  readonly root = new Container();
  readonly solver: PoseSolver;
  /** Ghosts carry their own bar because the shared course bar follows the viewport's player. */
  drawHeldLadderBar = false;
  private readonly heldLadderBar: Sprite;
  private readonly farLeg: Limb;
  private readonly farArm: Limb;
  private readonly waist: CapsuleShape;
  private readonly chest: CapsuleShape;
  private readonly sash: Sprite;
  private readonly sashTail: Ribbon;
  private readonly nearLeg: Limb;
  private readonly nearArm: Limb;
  private readonly neck: CapsuleShape;
  private readonly head: Sprite;
  private readonly band: Sprite;
  private readonly ribbonA: Ribbon;
  private readonly ribbonB: Ribbon;
  private readonly flash: Sprite;
  /** The halo behind the body and the mark on the ground under the feet, in the headband color. */
  private readonly halo: Sprite;
  private readonly contact: Sprite;
  /**
   * The rim: every body part again in ink, wider by the outline, all of them behind every fill, so
   * the whole silhouette carries one dark edge and stays legible over the sun and the lit edges.
   */
  private readonly rims: Limb[] = [];
  private readonly waistRim: CapsuleShape;
  private readonly chestRim: CapsuleShape;
  private readonly neckRim: CapsuleShape;
  private readonly headRim: Sprite;

  constructor(f: ShapeFactory, poses: Record<PoseName, Pose> = defaultPoses) {
    this.solver = new PoseSolver(poses);
    const r = this.root;
    this.halo = f.halo(r, 0, 0, 1, RED, 0);
    this.contact = f.disc(r, 0, 0, 1, RED, 0);
    this.heldLadderBar = f.disc(r, 0, 0, 1, NEON.bar);
    this.heldLadderBar.label = "held-ladder-bar";
    this.heldLadderBar.width = LADDER_BAR_WIDTH;
    this.heldLadderBar.height = LADDER_BAR_HEIGHT;
    this.heldLadderBar.visible = false;
    const limb = (color: number = BODY): Limb => ({
      upper: makeCapsule(f, r, color),
      lower: makeCapsule(f, r, color),
      tip: makeCapsule(f, r, color),
    });
    for (let i = 0; i < 4; i++) this.rims.push(limb(NEON.ink));
    this.waistRim = makeCapsule(f, r, NEON.ink);
    this.chestRim = makeCapsule(f, r, NEON.ink);
    this.neckRim = makeCapsule(f, r, NEON.ink);
    this.headRim = f.disc(r, 0, 0, 0.13, NEON.ink);
    this.farLeg = limb();
    this.farArm = limb();
    // The headband tails sit behind the body: when a stop whips them forward they pass behind
    // the head instead of across the face.
    this.ribbonA = new Ribbon(f, r, RED, 0, 0.05);
    this.ribbonB = new Ribbon(f, r, RED, 0.012, 0.042);
    this.waist = makeCapsule(f, r, BODY);
    this.chest = makeCapsule(f, r, BODY);
    this.sashTail = new Ribbon(f, r, RED, 0, 0.04);
    this.sash = f.line(r, RED, 0.07);
    this.nearLeg = limb();
    this.nearArm = limb();
    this.neck = makeCapsule(f, r, BODY);
    this.head = f.disc(r, 0, 0, 0.13, BODY);
    this.band = f.line(r, RED, 0.07);
    this.flash = f.ringAt(r, 0, 0, 1, RED, 0);
  }

  /** Recolor the body itself (the limbs, the torso, the neck, the head): paper for a figure on a dark screen. */
  setInk(color: number): void {
    const capsule = (c: CapsuleShape): void => {
      c.body.tint = color;
      c.top.tint = color;
      c.bottom.tint = color;
    };
    for (const limb of [this.farLeg, this.farArm, this.nearLeg, this.nearArm]) {
      capsule(limb.upper);
      capsule(limb.lower);
      capsule(limb.tip);
    }
    capsule(this.waist);
    capsule(this.chest);
    capsule(this.neck);
    this.head.tint = color;
  }

  /** Recolor the headband, its tails, the sash, the pump flash, the halo, and the ground mark: a player's color. */
  setAccent(color: number): void {
    this.band.tint = color;
    this.sash.tint = color;
    this.flash.tint = color;
    this.halo.tint = color;
    this.contact.tint = color;
    this.sashTail.setTint(color);
    this.ribbonA.setTint(color);
    this.ribbonB.setTint(color);
  }

  /** Keep the world-space ribbon chains continuous when a streamed world changes its origin. */
  shiftOrigin(offset: number): void {
    this.sashTail.shiftOrigin(offset);
    this.ribbonA.shiftOrigin(offset);
    this.ribbonB.shiftOrigin(offset);
  }

  private drawLeg(
    limb: Limb,
    rim: Limb,
    P: SkeletonPoints,
    feel: RenderFeel,
    left: boolean,
    scale: number,
  ): void {
    const o = feel.outline;
    const kx = left ? P.kneeLX : P.kneeRX;
    const ky = left ? P.kneeLY : P.kneeRY;
    const fx = left ? P.footLX : P.footRX;
    const fy = left ? P.footLY : P.footRY;
    const tx = left ? P.toeLX : P.toeRX;
    const ty = left ? P.toeLY : P.toeRY;
    setRimmed(limb.upper, rim.upper, P.hipX, P.hipY, kx, ky, feel.thighRadius * scale, o);
    setRimmed(limb.lower, rim.lower, kx, ky, fx, fy, feel.shinRadius * scale, o);
    // Boot: from a heel behind the ankle to the toe.
    const dx = tx - fx;
    const dy = ty - fy;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const hx = fx - (dx / d) * feel.footHeel;
    const hy = fy - (dy / d) * feel.footHeel;
    setRimmed(limb.tip, rim.tip, hx, hy, tx, ty, feel.footRadius * scale, o);
  }

  private drawArm(
    limb: Limb,
    rim: Limb,
    P: SkeletonPoints,
    feel: RenderFeel,
    left: boolean,
    scale: number,
  ): void {
    const o = feel.outline;
    const ex = left ? P.elbowLX : P.elbowRX;
    const ey = left ? P.elbowLY : P.elbowRY;
    const hx = left ? P.handLX : P.handRX;
    const hy = left ? P.handLY : P.handRY;
    setRimmed(limb.upper, rim.upper, P.shoulderX, P.shoulderY, ex, ey, feel.armRadius * scale, o);
    setRimmed(limb.lower, rim.lower, ex, ey, hx, hy, feel.forearmRadius * scale, o);
    // Fist: a short capsule past the wrist, wider than the forearm.
    const dx = hx - ex;
    const dy = hy - ey;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const hr = feel.handRadius * scale;
    setRimmed(limb.tip, rim.tip, hx, hy, hx + (dx / d) * hr * 0.5, hy + (dy / d) * hr * 0.5, hr, o);
  }

  draw(view: ViewState, sim: Sim, feel: RenderFeel, dt: number): void {
    this.heldLadderBar.visible =
      this.drawHeldLadderBar && view.barId >= 0 && sim.world.barLadder[view.barId]! >= 0;
    if (this.heldLadderBar.visible) this.heldLadderBar.position.set(view.handX, view.handY);
    const s = this.solver;
    s.update(view, sim, feel, dt);
    const P = s.points;
    // Failure: the body sinks and fades over the fail pause instead of standing frozen in the water.
    if (view.state === S.Fail) {
      const p = Math.min(1, view.stateTicks / Math.max(1, sim.tuning.failPauseTicks));
      this.root.alpha = 1 - p * p;
      this.root.position.set(0, -feel.failSink * p);
    } else {
      this.root.alpha = 1;
      this.root.position.set(0, 0);
    }
    const nearIsRight = P.fb >= 0;
    const fsign = P.fb < 0 ? -1 : 1;
    const tx = Math.sin(P.torsoAngle);
    const ty = Math.cos(P.torsoAngle);
    // Forward direction perpendicular to the torso axis, on screen.
    const fwdX = ty * fsign;
    const fwdY = -tx * fsign;

    // The halo sits behind the mass; the ground mark lies on the ground under the feet (found by a
    // probe down from the feet), wide and bright at contact, narrow and faint high up, gone over water.
    const failing = view.state === S.Fail;
    const glowOn = feel.bodyGlow > 0.01 && !failing;
    this.halo.visible = glowOn;
    if (glowOn) {
      this.halo.alpha = feel.bodyGlowAlpha;
      setDisc(this.halo, view.x, view.y + view.comY, feel.bodyGlow);
    }
    const r = sim.tuning.capsuleRadius;
    const ground =
      feel.contactAlpha > 0.001 && !failing
        ? sim.world.probeDown(view.x - r, view.x + r, view.y + 0.05, feel.contactFade + 0.5)
        : Number.NEGATIVE_INFINITY;
    const height = view.y - ground;
    const markOn = ground > Number.NEGATIVE_INFINITY && height < feel.contactFade;
    this.contact.visible = markOn;
    if (markOn) {
      const k = 1 - Math.max(0, height) / feel.contactFade;
      this.contact.alpha = feel.contactAlpha * (0.25 + 0.75 * k);
      this.contact.position.set(view.x, ground + 0.02);
      this.contact.width = 0.5 + 0.5 * k;
      this.contact.height = 0.1 + 0.06 * k;
    }

    // Far limbs first, slightly narrower.
    const o = feel.outline;
    this.drawLeg(this.farLeg, this.rims[0]!, P, feel, nearIsRight, 0.92);
    this.drawArm(this.farArm, this.rims[1]!, P, feel, nearIsRight, 0.9);

    // Torso: a narrow waist capsule from the hip up, a deeper chest capsule bulging forward, squashing
    // wider on landing.
    const squash = 1 + feel.landingSquash * Math.max(0, view.absorb / Math.max(1e-6, feel.landingMax));
    const torsoLen = BONES.torso;
    // The chest capsule's top disc ends at the shoulders so the head sits on a visible neck.
    const chestFrom = 0.4;
    const chestTo = 0.66;
    setRimmed(
      this.waist,
      this.waistRim,
      P.hipX,
      P.hipY,
      P.hipX + tx * torsoLen * 0.6,
      P.hipY + ty * torsoLen * 0.6,
      feel.waistRadius * squash,
      o,
    );
    setRimmed(
      this.chest,
      this.chestRim,
      P.hipX + tx * torsoLen * chestFrom + fwdX * feel.chestForward,
      P.hipY + ty * torsoLen * chestFrom + fwdY * feel.chestForward,
      P.hipX + tx * torsoLen * chestTo + fwdX * feel.chestForward,
      P.hipY + ty * torsoLen * chestTo + fwdY * feel.chestForward,
      feel.chestRadius * squash,
      o,
    );
    // Sash: a band around the waist just above the hip, with a short tail streaming behind.
    const sashY = 0.09;
    const cxs = P.hipX + tx * sashY;
    const cys = P.hipY + ty * sashY;
    const perpX = ty;
    const perpY = -tx;
    const sw = feel.waistRadius * squash + 0.025;
    this.sash.position.set(cxs - perpX * sw, cys - perpY * sw);
    this.sash.rotation = Math.atan2(perpY, perpX);
    this.sash.width = 2 * sw;
    this.sash.height = feel.sashWidth;
    this.sashTail.widthBase = feel.sashWidth * 0.55;
    this.sashTail.segmentCount = Math.round(feel.sashTailSegments);
    this.sashTail.update(cxs - fwdX * sw, cys - fwdY * sw, -fwdX * 0.6, -0.8, feel, dt);

    this.drawLeg(this.nearLeg, this.rims[2]!, P, feel, !nearIsRight, 1);
    this.drawArm(this.nearArm, this.rims[3]!, P, feel, !nearIsRight, 1);

    // Neck, head, headband at eye level, tails from the back of the head.
    const hx = Math.sin(P.headAngle);
    const hy = Math.cos(P.headAngle);
    setRimmed(
      this.neck,
      this.neckRim,
      P.neckX - tx * 0.05,
      P.neckY - ty * 0.05,
      P.neckX + hx * 0.06,
      P.neckY + hy * 0.06,
      0.055,
      o,
    );
    const hr = feel.headRadius;
    setDisc(this.head, P.headX, P.headY, hr);
    setDisc(this.headRim, P.headX, P.headY, hr + o);
    const bandUp = feel.bandUp;
    const bx0 = P.headX + hx * bandUp - hy * hr;
    const by0 = P.headY + hy * bandUp + hx * hr;
    const bx1 = P.headX + hx * bandUp + hy * hr;
    const by1 = P.headY + hy * bandUp - hx * hr;
    this.band.position.set(bx0, by0);
    this.band.rotation = Math.atan2(by1 - by0, bx1 - bx0);
    this.band.width = Math.hypot(bx1 - bx0, by1 - by0);
    this.band.height = feel.bandWidth;
    const backX = P.headX + hx * bandUp - fsign * hy * hr * 0.9;
    const backY = P.headY + hy * bandUp + fsign * hx * hr * 0.9;
    this.ribbonA.widthBase = feel.ribbonWidth;
    this.ribbonB.widthBase = feel.ribbonWidth * 0.85;
    this.ribbonA.update(backX, backY + 0.015, -fsign * 0.8, -0.3, feel, dt);
    this.ribbonB.update(backX, backY - 0.015, -fsign * 0.7, -0.5, feel, dt);
    // PERFECT pump: a red ring rings out from the hands and fades.
    const sinceFlashMs = ((view.tick - view.lastPumpTick) * 1000) / 120;
    const flashing = view.lastPumpGrade === 4 && sinceFlashMs >= 0 && sinceFlashMs < feel.flashMs;
    const flashT = sinceFlashMs / feel.flashMs;
    this.flash.alpha = flashing ? 0.85 * (1 - flashT) : 0;
    if (flashing) {
      const cx = (P.handLX + P.handRX) * 0.5;
      const cy = (P.handLY + P.handRY) * 0.5;
      setDisc(this.flash, cx, cy, 0.25 + 0.7 * flashT);
    }
  }
}
